/**
 * Product result card.
 *
 * Information hierarchy is fixed by spec section 26:
 *   1. Product  2. Price  3. Quantity  4. Primary value metric
 *   5. Protein/calories  6. Store
 *
 * The primary value metric is whatever the active sort ranks by, so the card
 * always shows the number that explains its position.
 */

import { Link } from 'react-router-dom';
import type { MetricId } from '@/lib/nutrition/calculations';
import { METRICS, primaryPriceMetric } from '@/lib/nutrition/calculations';
import type { AnalysedProduct } from '@/lib/nutrition/ranking';
import { winningMetrics } from '@/lib/nutrition/ranking';
import { formatCurrency, formatMetricValue, formatPackage, metricTitle, metricUnit } from '@/lib/format';
import { MacroBar } from './MacroBar';
import { DataQualityBadge } from './DataQualityBadge';

interface Props {
  row: AnalysedProduct;
  /** The metric the active sort uses — shown as the hero number. */
  primaryMetric: MetricId | 'valueScore';
  isComparing: boolean;
  compareDisabled: boolean;
  onToggleCompare: (id: string, name: string) => void;
}

export function ProductCard({
  row,
  primaryMetric,
  isComparing,
  compareDisabled,
  onToggleCompare,
}: Props): React.JSX.Element {
  const { product, metrics, score, rank } = row;
  const currency = metrics.currency;
  const price = metrics.values.price;

  const heroMetric: MetricId =
    primaryMetric === 'valueScore' ? 'proteinPerDollar' : primaryMetric;
  const heroValue =
    primaryMetric === 'valueScore'
      ? { value: score.score, unavailableReason: score.unavailableReason }
      : metrics.values[heroMetric];

  const normalizedPrice = primaryPriceMetric(metrics.packageKind);

  // Podium treatment for the top three. The numeral stays visible, so rank is
  // never communicated by colour alone.
  const podium = rank !== null && rank <= 3 ? rank : null;
  const wins = winningMetrics(row);

  return (
    <article className={`card${podium !== null ? ` is-podium-${podium}` : ''}`}>
      <div className="card-head">
        {/* Rank is a numeral, never colour alone. */}
        <div className={`rank${podium !== null ? ` rank-medal-${podium}` : ''}`} aria-label={rank === null ? 'Not ranked' : `Rank ${rank}`}>
          {rank === null ? '—' : rank}
        </div>

        <div className="card-title">
          <Link
            className="card-name"
            to={`/product/${encodeURIComponent(product.id)}`}
            state={{ product }}
          >
            {product.name}
          </Link>
          <div className="card-brand">
            {product.brand ?? 'Unbranded'}
            {product.quantity && ` · ${formatPackage(product.quantity)}`}
          </div>
        </div>

        <div className="card-price">
          <div className="price-amount">{formatCurrency(price.value, currency)}</div>
          {product.price?.originalAmount != null && product.price.originalAmount > (price.value ?? 0) && (
            <div className="price-was">{formatCurrency(product.price.originalAmount, currency)}</div>
          )}
          <div className="price-unit">
            {metrics.values[normalizedPrice].value === null
              ? 'unit price N/A'
              : `${formatMetricValue(normalizedPrice, metrics.values[normalizedPrice], currency)} ${metricUnit(normalizedPrice, currency).replace(/^.*?\/\s*/, '/ ')}`}
          </div>
        </div>
      </div>

      {/* The number that justifies this card's position in the list. */}
      <div className="hero-metric">
        <span className="hero-metric-label">
          {primaryMetric === 'valueScore' ? 'Value score' : METRICS[heroMetric].label}
        </span>
        <span
          className={`hero-metric-value${heroValue?.value === null ? ' na' : ''}`}
          title={
            primaryMetric === 'valueScore'
              ? (score.unavailableReason ?? 'Composite score across these results.')
              : metricTitle(heroMetric, heroValue)
          }
        >
          {heroValue?.value === null
            ? 'N/A'
            : primaryMetric === 'valueScore'
              ? String(score.score)
              : `${formatMetricValue(heroMetric, heroValue, currency, metrics.packageKind)} ${metricUnit(heroMetric, currency)}`}
        </span>
      </div>

      <div className="metric-grid">
        <MetricCell row={row} metric="protein100" />
        <MetricCell row={row} metric="kcal100" />
        <MetricCell row={row} metric="proteinPerDollar" />
      </div>

      <MacroBar macros={metrics.macros} compact />

      {wins.length > 0 && (
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {wins.slice(0, 2).map((metric) => (
            <span key={metric} className="badge badge-accent">
              #1 {METRICS[metric].label}
            </span>
          ))}
        </div>
      )}

      <div className="card-foot">
        <span className="store-name">{product.store?.name ?? 'Unknown store'}</span>
        {product.store?.location && <span>· {product.store.location}</span>}
        <span className="badge badge-neutral">
          {product.store?.online === false ? 'In store' : 'Online'}
        </span>
        <DataQualityBadge quality={product.quality} />

        <div className="card-actions">
          <button
            type="button"
            className={`btn btn-sm${isComparing ? ' btn-primary' : ''}`}
            onClick={() => onToggleCompare(product.id, product.name)}
            disabled={!isComparing && compareDisabled}
            aria-pressed={isComparing}
            title={
              !isComparing && compareDisabled
                ? 'The comparison tray is full. Remove a product first.'
                : undefined
            }
          >
            {isComparing ? '✓ Comparing' : 'Compare'}
          </button>
        </div>
      </div>
    </article>
  );
}

function MetricCell({ row, metric }: { row: AnalysedProduct; metric: MetricId }): React.JSX.Element {
  const value = row.metrics.values[metric];
  const currency = row.metrics.currency;
  const unavailable = value.value === null;
  return (
    <div className="metric-cell" title={metricTitle(metric, value)}>
      <div className="metric-cell-label">
        {METRICS[metric].label}
        {!unavailable && metric !== 'protein100' && metric !== 'kcal100'
          ? ''
          : metric === 'protein100'
            ? ' /100'
            : ' /100'}
      </div>
      <div className={`metric-cell-value${unavailable ? ' na' : ''}`}>
        {unavailable
          ? 'N/A'
          : formatMetricValue(metric, value, currency, row.metrics.packageKind)}
      </div>
    </div>
  );
}
